import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { PropTypes } from 'prop-types';

import AccessControl from 'components/AccessControl';
import ActivityPerms from 'components/AccessControl/permissions.json';

import valueOrEmpty from 'common/App/valueOrEmpty';
import StatementTypes from 'common/Forms/StatementTypes';

import ValidationInspector from 'common/Validation/ValidationInspector';
import ImyChapter3Status from 'common/Forms/ImyChapter3Status';
import Chapter4Status from 'common/Customers/Chapter4Status';
import WhdValidationSummary from 'components/WhdStatement/WhdValidationSummary';
import JurisdictionDropDown from 'components/Common/Inputs/JurisdictionDropDown';
import { updateStatement } from 'actions';

const IntermediaryDetailsPanel = ({ accountId, statement, getChapter3DescriptionByName, getChapter4DescriptionByName, permissions, dispatch, validationResult, showingVersion }) => {
  const isDetailed = statement && statement.type === StatementTypes.DetailedWhdStatement;
  const ignoredValidationCodes = ['REJ600070'];
  const hasWarnings = ValidationInspector.hasWarnings(validationResult, null, ignoredValidationCodes);
  const hasRejections = ValidationInspector.hasRejections(validationResult, null, ignoredValidationCodes);
  const canEdit = AccessControl.hasPermission(permissions, [ActivityPerms.multiTierMgrEditWhs]) && !showingVersion;
  const [isEdit, setIsEdit] = useState(false);
  const [intermediaryName, setIntermediaryName] = useState(statement.intermediaryName);
  const [intermediaryIdentifier, setIntermediaryIdentifier] = useState(statement.intermediaryIdentifier);
  const [intermediaryAddressLine1, setIntermediaryAddressLine1] = useState(statement.intermediaryAddressLine1);
  const [intermediaryCity, setIntermediaryCity] = useState(statement.intermediaryCity);
  const [intermediaryCountry, setIntermediaryCountry] = useState(statement.intermediaryCountry);
  const [intermediaryGiin, setIntermediaryGiin] = useState(statement.intermediaryGiin);
  const [intermediaryTin, setIntermediaryTin] = useState(statement.intermediaryTin);
  const [intermediaryFatcaStatus, setIntermediaryFatcaStatus] = useState(Chapter4Status.findByName(statement.intermediaryFatcaStatus));
  const [intermediaryChapter3, setIntermediaryChapter3] = useState(ImyChapter3Status.findByName(statement.intermediaryChapter3));
  const [hybridStatus, setHybridStatus] = useState(statement.hybridStatus);
  const { isSavingStatement } = useSelector(state => state && state.withholdingStatement);

  const buildAddress = (addressElements) => {
    if (addressElements && addressElements.length > 0) {
      return addressElements.reduce((prev, curr) => {
        if (curr && curr.length > 0) {
          return `${ prev && prev.length > 0 ? `${ prev }, ` : '' }${ curr }`;
        }

        return prev ?? null;
      });
    }

    return null;
  };

  const submitData = () => {
    const obj = {
      details: {
        intermediaryIdentifier,
        intermediaryName,
        intermediaryChapter3: intermediaryChapter3?.name,
        intermediaryFatcaStatus: intermediaryFatcaStatus?.name,
        intermediaryGiin
      }
    };

    if (isDetailed) {
      obj.details.intermediaryAddressLine1 = intermediaryAddressLine1;
      obj.details.intermediaryCity = intermediaryCity;
      obj.details.intermediaryCountry = intermediaryCountry;
      obj.details.intermediaryTin = intermediaryTin;
      obj.details.hybridStatus = hybridStatus;
    }

    setIsEdit(false);
    dispatch(updateStatement(accountId, obj));
  };

  return <div id="intermediary-details" className={ `panel p-2 full-height mr-2 ${ (hasWarnings || hasRejections) ? ' validation-error-panel' : '' }` }>
    <div className="panel-header"><h2>Intermediary Details</h2></div>
    <div className="panel-body pt-2">
      <div className="columns intermediary-details">
        <div className="col-6">
          <div className="columns">
            <div className="col-4 panel-item-name">Institution Name</div>
            { !isEdit && <div className="col-8 panel-item-value">{ statement.intermediaryName }</div>}
            { isEdit && <input type="text" className="form-input" onChange={ e => setIntermediaryName(e.target.value) } value={ intermediaryName } /> }
          </div>
          <div className="columns">
            <div className="col-4 panel-item-name">Unique ID</div>
            { !isEdit && <div className="col-8 panel-item-value">{ valueOrEmpty(statement.intermediaryIdentifier) }</div> }
            { isEdit && <input type="text" className="form-input" onChange={ e => setIntermediaryIdentifier(e.target.value) } value={ intermediaryIdentifier ?? '' } /> }
          </div>
          { isDetailed && <div className="columns">
            <div className="col-4 panel-item-name">Institution Address</div>
            { !isEdit && <div className="col-8 panel-item-value">{ valueOrEmpty(buildAddress([statement.intermediaryAddressLine1, statement.intermediaryCity, statement.intermediaryCountry])) }</div> }
            { isEdit && <input type="text" className="form-input" placeholder="Address" onChange={ e => setIntermediaryAddressLine1(e.target.value) } value={ intermediaryAddressLine1 } /> }
            { isEdit && <input type="text" className="form-input" placeholder="City" onChange={ e => setIntermediaryCity(e.target.value) } value={ intermediaryCity } /> }
            { isEdit && <JurisdictionDropDown value={ intermediaryCountry } onChange={ value => setIntermediaryCountry(value) } /> }
          </div> }
        </div>
        <div className="col-6">
          <div className="columns">
            <div className="col-4 panel-item-name">
              Intermediary GIIN
            </div>
            <div className="col-8 panel-item-value">
              { !isEdit && valueOrEmpty(statement.intermediaryGiin) }
              { isEdit && <input type="text" className="form-input" onChange={ e => setIntermediaryGiin(e.target.value) } value={ intermediaryGiin } /> }
            </div>
            { isDetailed && <React.Fragment>
              <div className="col-4 panel-item-name">
                Intermediary EIN
              </div>
              <div className="col-8 panel-item-value">
                { !isEdit && valueOrEmpty(statement.intermediaryTin) }
                { isEdit && <input type="text" className="form-input" onChange={ e => setIntermediaryTin(e.target.value) } value={ intermediaryTin } /> }

              </div>
            </React.Fragment> }
            <div className="col-4 panel-item-name">
              FATCA Status
            </div>
            <div className="col-8 panel-item-value">
              { !isEdit && valueOrEmpty(getChapter4DescriptionByName(statement.intermediaryFatcaStatus, false)) }
              { isEdit && <select className="form-select" onChange={ e => setIntermediaryFatcaStatus(Chapter4Status.find(x => x.value === parseInt(e.target.value, 10))) } defaultValue={ intermediaryFatcaStatus && intermediaryFatcaStatus.value }>
                <option>Please select</option>
                { Chapter4Status.map(x => (
                  <option key={ x.value } value={ x.value }>{ x.description }</option>
                )) }
              </select> }
            </div>
            <div className="col-4 panel-item-name">
              Chapter 3 Entity Type
            </div>
            <div className="col-8 panel-item-value">
              { !isEdit && valueOrEmpty(getChapter3DescriptionByName(statement.intermediaryChapter3, false)) }
              { isEdit && <select className="form-select" onChange={ e => setIntermediaryChapter3(ImyChapter3Status.find(x => x.value === parseInt(e.target.value, 10))) } defaultValue={ intermediaryChapter3 && intermediaryChapter3.value }>
                <option>Please select</option>
                { ImyChapter3Status.map(x => (
                  <option key={ x.value } value={ x.value }>{ x.description }</option>
                )) }
              </select> }
            </div>
            { isDetailed && <React.Fragment>
              <div className="col-4 panel-item-name">
                Hybrid Status
              </div>
              <div className="col-8 panel-item-value">
                { !isEdit && valueOrEmpty(statement.hybridStatus) }
                { isEdit && <select className="form-select" onChange={ e => setHybridStatus(e.target.value) } value={ hybridStatus || '' }>
                  <option value="">Please select</option>
                  <option>Hybrid</option>
                  <option>Reverse Hybrid</option>
                  <option>N/A</option>
                </select> }
              </div>
            </React.Fragment> }
          </div>
        </div>
      </div>
    </div>
    <div className="panel-footer align-vertical-middle space-between m-2">
      <WhdValidationSummary sectionTitle="Intermediary Details" validationResult={ validationResult } ignoreCodes={ ignoredValidationCodes } className="ml-2" />
      { canEdit && <div className={ `action-buttons ${ (!validationResult || (!validationResult || !(hasRejections || hasWarnings))) ? 'col-12' : '' }` }>
        { !isEdit && <button className="btn btn-secondary" type="button" disabled={ isSavingStatement } onClick={ () => setIsEdit(!isEdit) }><span className="ag-icon-tick" /><i className="icon icon-2x icon-edit" /></button> }
        { isEdit && <button className="btn btn-secondary" type="button" disabled={ isSavingStatement } onClick={ () => submitData() }><i className="icon icon-2x icon-check" /></button> }
        { isEdit && <button className="btn btn-secondary" type="button" disabled={ isSavingStatement } onClick={ () => setIsEdit(!isEdit) }><i className="icon icon-2x icon-cross" /></button> }
      </div> }
    </div>
  </div>;
};

IntermediaryDetailsPanel.propTypes = {
  statement: PropTypes.shape({
    hybridStatus: PropTypes.string,
    intermediaryAddressLine1: PropTypes.string,
    intermediaryChapter3: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number
    ]),
    intermediaryCity: PropTypes.string,
    intermediaryCountry: PropTypes.string,
    intermediaryFatcaStatus: PropTypes.string,
    intermediaryGiin: PropTypes.string,
    intermediaryIdentifier: PropTypes.string,
    intermediaryName: PropTypes.string,
    intermediaryTin: PropTypes.string,
    type: PropTypes.number,
  }),
  getChapter3DescriptionByName: PropTypes.func.isRequired,
  getChapter4DescriptionByName: PropTypes.func.isRequired,
  validationResult: PropTypes.shape({}),
  accountId: PropTypes.string.isRequired,
  permissions: PropTypes.arrayOf(PropTypes.string),
  dispatch: PropTypes.func.isRequired,
  showingVersion: PropTypes.bool
};

IntermediaryDetailsPanel.defaultProps = {
  statement: null,
  validationResult: null,
  permissions: null,
  showingVersion: false
};

export default IntermediaryDetailsPanel;
